import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { clearSession, getSession, authHeaders, API_BASE_URL } from "./apiConfig";

const MENU = [
  {
    section: "Master",
    items: [
      { label: "Party Master", to: "/party-master", screenKey: "party-master" },
      { label: "Product Master", to: "/product-master", screenKey: "product-master" },
      { label: "Employee Master", to: "/admin/Employee", screenKey: "employee-master" },
      { label: "Company Master", to: null },
      { label: "Tax Master", to: null },
      { label: "Parameters", to: null },
    ],
  },
  {
    section: "Transactions",
    items: [
      { label: "AWB In Entry", to: "/admin/AwbIn", screenKey: "awb-in" },
      { label: "AWB Out Entry", to: null },
      { label: "Party Transaction", to: "/admin/partytransaction", screenKey: "party-transaction" },
      { label: "Create Bill (Legacy)", to: "/admin/CreateBill", screenKey: "party-transaction" },
      { label: "Create Bill with SP (Legacy)", to: "/admin/CreateBillWithSp", screenKey: "party-transaction" },
    ],
  },
  {
    section: "Reports",
    items: [
      { label: "Invoice Print", to: "/reports", screenKey: "reports" },
      { label: "AWB List Print", to: null },
      { label: "Ledger", to: "/reports", screenKey: "reports" },
    ],
  },
  {
    section: "Final Reports",
    items: [
      { label: "Opening Trial Balance", to: null },
      { label: "Trial Balance", to: null },
      { label: "Profit & Loss A/c", to: "/reports", screenKey: "reports" },
      { label: "Balance Sheet", to: "/reports", screenKey: "reports" },
    ],
  },
  {
    section: "GST Reports",
    items: [{ label: "GST Excel Sheet", to: null }],
  },
  {
    section: "Other Reports",
    items: [
      { label: "Customer List", to: null },
      { label: "Employee List", to: null },
    ],
  },
  {
    section: "Utility",
    items: [
      { label: "Backup", to: null },
      { label: "Recalculations", to: null },
      { label: "WhatsApp Message", to: null },
    ],
  },
  {
    section: "Administration",
    items: [
      { label: "Account Management", to: "/admin/Account", screenKey: "account-management" },
      { label: "User Management", to: "/admin/CreateUser", screenKey: "user-management" },
      { label: "Role Management", to: "/admin/RoleManagement", screenKey: "role-management" },
    ],
  },
];

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const session = getSession();

  const [openSections, setOpenSections] = useState(
    MENU.reduce((acc, s) => ({ ...acc, [s.section]: true }), {})
  );
  const [viewableScreens, setViewableScreens] = useState(null); // null = still loading

  // Load this user's permissions once, based on their role (from the JWT token)
  useEffect(() => {
    if (!session?.token) {
      setViewableScreens(new Set()); // not logged in, show nothing gated
      return;
    }
    fetch(`${API_BASE_URL}/permission/my`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        const allowed = new Set(rows.filter((r) => r.canView).map((r) => r.screenKey));
        setViewableScreens(allowed);
      })
      .catch(() => setViewableScreens(new Set()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSection = (section) =>
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));

  const handleLogout = () => {
    clearSession();
    navigate("/login");
  };

 // A menu item is shown ONLY if it has a real route AND a screenKey the current
  // role has View rights for. No screenKey = no permission to check = never shown,
  // to anyone, until the page is actually built and added to Role Management.
  const canSeeItem = (item) => {
    if (!item.to || !item.screenKey) return false;
    if (viewableScreens === null) return false; // still loading, don't flash items
    return viewableScreens.has(item.screenKey);
  };

  return (
    <div className="admin-layout">
      <style>{`
        .admin-layout { display: flex; min-height: 100vh; background: #f8fafc; font-family: sans-serif; }
        .sidebar { width: 270px; background: #0f172a; color: white; display: flex; flex-direction: column; overflow-y: auto; }
        .sidebar-header { padding: 1.25rem 1.5rem; font-weight: bold; font-size: 1.1rem; border-bottom: 1px solid #1e293b; display: flex; justify-content: space-between; align-items: center; }
        .sidebar-user { padding: 0.75rem 1.5rem; font-size: 0.75rem; color: #64748b; border-bottom: 1px solid #1e293b; }
        .menu-section-title { padding: 0.9rem 1.25rem 0.4rem; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; cursor: pointer; display: flex; justify-content: space-between; align-items: center; user-select: none; }
        .menu-section-title:hover { color: #94a3b8; }
        .nav-item { padding: 0.55rem 1.25rem 0.55rem 1.75rem; color: #cbd5e1; text-decoration: none; display: block; font-size: 0.875rem; border-left: 3px solid transparent; }
        .nav-item:hover, .nav-item.active { background: #1e293b; color: white; border-left: 3px solid #3b82f6; }
        .nav-item.disabled { color: #475569; cursor: not-allowed; }
        .nav-item.disabled:hover { background: none; border-left: 3px solid transparent; color: #475569; }
        .coming-soon-tag { font-size: 0.65rem; background: #334155; color: #94a3b8; padding: 1px 6px; border-radius: 10px; margin-left: 6px; }
        .main-content { flex-grow: 1; padding: 2rem; overflow-x: auto; }
        .card { background: white; padding: 1.5rem; border-radius: 8px; border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
        .logout-btn { margin: 1rem 1.25rem; padding: 0.6rem; background: none; border: 1px solid #334155; color: #ef4444; border-radius: 6px; cursor: pointer; font-size: 0.875rem; }
        .logout-btn:hover { background: #1e293b; }
      `}</style>

      <aside className="sidebar">
        <div className="sidebar-header">
          <Link to="/admin" style={{ color: "white", textDecoration: "none" }}>
            ExpressLogistics
          </Link>
        </div>

        {session && (
          <div className="sidebar-user">
            {session.User_Name} · Client {session.Client_ID} · {session.Role}
          </div>
        )}

        <Link to="/admin" className={`nav-item ${location.pathname === "/admin" ? "active" : ""}`}>
          Dashboard
        </Link>

        {MENU.map((sec) => {
          const visibleItems = sec.items.filter(canSeeItem);
          if (visibleItems.length === 0) return null; // hide whole section if nothing to show

          return (
            <div key={sec.section}>
              <div className="menu-section-title" onClick={() => toggleSection(sec.section)}>
                {sec.section}
                <span>{openSections[sec.section] ? "−" : "+"}</span>
              </div>
              {openSections[sec.section] &&
                visibleItems.map((item) =>
                  item.to ? (
                    <Link
                      key={item.label}
                      to={item.to}
                      className={`nav-item ${location.pathname === item.to ? "active" : ""}`}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span key={item.label} className="nav-item disabled">
                      {item.label}
                      <span className="coming-soon-tag">Soon</span>
                    </span>
                  )
                )}
            </div>
          );
        })}

        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </aside>

      <main className="main-content">{children}</main>
    </div>
  );
};

export default AdminLayout;